import React from 'react'

// A from-scratch reimplementation of HeroUI Pro's Widget anatomy (Header,
// Title, Description, Content, Footer, Legend) using only the free
// component set -- Pro isn't installed here. The outer shell is a flat
// bg-surface-secondary (no border), and Content nests on bg-surface (the
// same Surface variant="default" the KPI tiles use) so the chart container
// reads as a card sitting inside the widget shell. Both radii are
// rounded-2xl: the root matches the Surface wrapping a Table elsewhere in
// the app, and Content matches var(--radius-2xl), the radius HeroUI's own
// Table applies to its header/body's first and last cells (see Table's
// styles.css), so a chart card and a table card read as the same shape
// language. Root's own padding (the shell reveal around Content) is kept
// thin, p-1, to match how little space a Surface leaves around a Table.
// Legend renders inside Content (below the chart, on its
// surface) rather than in Footer, which stays available as a
// general-purpose slot outside Content. Root is flex-col and Content is
// flex-1 so that when a grid row stretches one widget taller than its own
// natural height (e.g. next to a taller sibling card), Content absorbs
// that extra height itself instead of leaving empty shell showing below
// an unstretched content box. Mirrors admin-dashboard's Widget.tsx so
// charts look identical across both apps.

function WidgetRoot({ className = '', children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`flex flex-col rounded-2xl bg-surface-secondary p-1 ${className}`} {...props}>
      {children}
    </div>
  )
}

function WidgetHeader({ className = '', children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`flex items-center justify-between gap-3 px-4 pt-2 pb-2 ${className}`} {...props}>
      {children}
    </div>
  )
}

function WidgetTitle({ className = '', children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={`text-sm sm:text-base font-medium text-foreground ${className}`} {...props}>
      {children}
    </h3>
  )
}

function WidgetDescription({ className = '', children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={`mt-0.5 text-xs text-muted ${className}`} {...props}>
      {children}
    </p>
  )
}

function WidgetContent({ className = '', children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`flex-1 rounded-2xl bg-surface p-4 ${className}`} {...props}>
      {children}
    </div>
  )
}

function WidgetFooter({ className = '', children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`flex items-center justify-between gap-3 pt-3 ${className}`} {...props}>
      {children}
    </div>
  )
}

function WidgetLegend({ className = '', children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`} {...props}>
      {children}
    </div>
  )
}

function WidgetLegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted">
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </div>
  )
}

export const Widget = Object.assign(WidgetRoot, {
  Header: WidgetHeader,
  Title: WidgetTitle,
  Description: WidgetDescription,
  Content: WidgetContent,
  Footer: WidgetFooter,
  Legend: Object.assign(WidgetLegend, { Item: WidgetLegendItem }),
})

export default Widget
